import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthToken, setAuthToken, removeAuthToken } from '@/lib/api';
import { signInWithEmail, signUpWithEmail, firebaseSignOut, onFirebaseAuthStateChanged } from '@/lib/firebase';

interface User {
  email: string;
  displayName: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored token/user first
    const token = getAuthToken();
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        removeAuthToken();
        localStorage.removeItem('user');
      }
    }

    // Listen for Firebase auth state changes
    const unsubscribe = onFirebaseAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        setAuthToken(idToken);
        const userData = { 
          email: firebaseUser.email || '', 
          displayName: firebaseUser.displayName || '' 
        };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }
      setIsLoading(false);
    });

    // If no Firebase listener fires within a short time, stop loading
    const timeout = setTimeout(() => setIsLoading(false), 1000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const { idToken, email: userEmail, displayName } = await signInWithEmail(email, password);
    setAuthToken(idToken);
    const userData = { email: userEmail, displayName };
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const signup = async (email: string, password: string, displayName: string): Promise<void> => {
    const { idToken, email: userEmail, displayName: name } = await signUpWithEmail(email, password, displayName);
    setAuthToken(idToken);
    const userData = { email: userEmail, displayName: name };
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = (): void => {
    removeAuthToken();
    localStorage.removeItem('user');
    setUser(null);
    firebaseSignOut().catch(console.error);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
